import { Fragment, useMemo, useRef, useState, type ReactNode } from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { useIsDesktop, useIsPhone, useIsWide, useListNavigation } from '@/lib/hooks';
import { VIRTUALIZE_AFTER_ROWS } from '@/theme/tokens';
import { Skeleton } from './Skeleton';
import { DefinitionPopover } from './DefinitionPopover';

export type ColumnPriority = 1 | 2 | 3 | 4 | 5;

export interface Column<T> {
  id: string;
  header: string;
  /** 1 is what a person needs to identify and act on the row. See DESIGN.md section 5. */
  priority: ColumnPriority;
  align?: 'left' | 'right';
  render: (row: T) => ReactNode;
  sortable?: boolean;
  mono?: boolean;
  /** Money columns are absent for a packer because the server omits the field, not the column. */
  money?: boolean;
  width?: string;
  /**
   * The one sentence definition of what this column holds, from /legend. Rendered as a
   * DefinitionPopover on the header. Omitted when the legend has no entry for the column.
   */
  hint?: string | null;
}

export interface DataTableSort {
  by: string;
  dir: 'asc' | 'desc';
}

export interface DataTableProps<T, K extends string | number = string> {
  columns: readonly Column<T>[];
  rows: readonly T[];
  /** Stable identity per row. Strings or numbers both work; selection is keyed by it. */
  rowKey: (row: T) => K;
  onRowClick?: (row: T) => void;
  selection?: {
    selected: ReadonlySet<K>;
    onToggle: (key: K) => void;
  };
  emptyState: ReactNode;
  caption: string;
  loading?: boolean;
  sort?: DataTableSort;
  onSortChange?: (sort: DataTableSort) => void;
  /** Ledger and reconciliation tables only. Adds the allowlisted inner scroll container. */
  allowHorizontalScroll?: boolean;
  estimatedRowHeight?: number;
}

/**
 * One table component for the whole product.
 *
 * Above 1280px every column renders. Priority 5 drops at 1280, priority 4 at 1024, and both
 * stay reachable in the row expander. Below 768px the table becomes a card list built from
 * the column priorities, because a table that scrolls sideways on a phone is how a packer
 * marks the wrong order packed.
 *
 * Past 60 rows the body virtualizes against the window scroller.
 */
export function DataTable<T, K extends string | number = string>({
  columns,
  rows,
  rowKey,
  onRowClick,
  selection,
  emptyState,
  caption,
  loading = false,
  sort,
  onSortChange,
  allowHorizontalScroll = false,
  estimatedRowHeight,
}: DataTableProps<T, K>) {
  const isPhone = useIsPhone();
  const isDesktop = useIsDesktop();
  const isWide = useIsWide();
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set<string>());

  const hidden = useMemo(() => {
    if (isPhone) return columns.filter((c) => c.priority >= 3);
    return columns.filter((c) => (c.priority === 5 && !isWide) || (c.priority === 4 && !isDesktop));
  }, [columns, isDesktop, isPhone, isWide]);

  const hiddenIds = useMemo(() => new Set(hidden.map((c) => c.id)), [hidden]);
  const visible = useMemo(() => columns.filter((c) => !hiddenIds.has(c.id)), [columns, hiddenIds]);

  const toggleExpanded = (key: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const nav = useListNavigation({
    count: rows.length,
    onOpen: (index) => {
      const row = rows[index];
      if (row && onRowClick) onRowClick(row);
    },
    onToggleSelect: (index) => {
      const row = rows[index];
      if (row && selection) selection.onToggle(rowKey(row));
    },
  });

  if (loading) {
    return <Skeleton shape="row" lines={6} label={`Loading ${caption}`} />;
  }
  if (rows.length === 0) {
    return <>{emptyState}</>;
  }

  const shared = {
    rows,
    rowKey,
    onRowClick,
    selection,
    expanded,
    toggleExpanded,
    nav,
    estimatedRowHeight,
  };

  if (isPhone) {
    return (
      <CardList
        {...shared}
        columns={columns}
        caption={caption}
        sort={sort}
        onSortChange={onSortChange}
      />
    );
  }

  return (
    <div
      className={allowHorizontalScroll ? 'o-table-wrap o-table-scroll' : 'o-table-wrap'}
      {...(allowHorizontalScroll ? { 'data-allow-hscroll': 'true' } : {})}
    >
      <TableBody
        {...shared}
        columns={visible}
        hiddenColumns={hidden}
        caption={caption}
        sort={sort}
        onSortChange={onSortChange}
      />
    </div>
  );
}

interface BodyProps<T, K extends string | number> {
  columns: readonly Column<T>[];
  hiddenColumns?: readonly Column<T>[];
  rows: readonly T[];
  rowKey: (row: T) => K;
  onRowClick?: (row: T) => void;
  selection?: { selected: ReadonlySet<K>; onToggle: (key: K) => void };
  expanded: ReadonlySet<string>;
  toggleExpanded: (key: string) => void;
  caption: string;
  sort?: DataTableSort;
  onSortChange?: (sort: DataTableSort) => void;
  nav: ReturnType<typeof useListNavigation>;
  estimatedRowHeight?: number;
}

function TableBody<T, K extends string | number>({
  columns,
  hiddenColumns = [],
  rows,
  rowKey,
  onRowClick,
  selection,
  expanded,
  toggleExpanded,
  caption,
  sort,
  onSortChange,
  nav,
  estimatedRowHeight = 56,
}: BodyProps<T, K>) {
  const parentRef = useRef<HTMLTableSectionElement | null>(null);
  const virtualize = rows.length > VIRTUALIZE_AFTER_ROWS;

  const virtualizer = useWindowVirtualizer({
    count: rows.length,
    estimateSize: () => estimatedRowHeight,
    overscan: 12,
    enabled: virtualize,
    scrollMargin: parentRef.current?.offsetTop ?? 0,
  });

  const items = virtualize ? virtualizer.getVirtualItems() : null;
  const paddingTop = items && items.length > 0 ? (items[0]?.start ?? 0) : 0;
  const paddingBottom =
    items && items.length > 0
      ? virtualizer.getTotalSize() - (items[items.length - 1]?.end ?? 0)
      : 0;

  const indexes = items ? items.map((item) => item.index) : rows.map((_row, index) => index);
  const totalCols = columns.length + (selection ? 1 : 0) + (hiddenColumns.length > 0 ? 1 : 0);

  return (
    // The container carries the list keys so j and k work only when focus is in the list.
    <table className="o-table" onKeyDown={nav.onKeyDown} tabIndex={-1}>
      <caption className="o-visually-hidden">{caption}</caption>
      <thead>
        <tr>
          {selection ? (
            <th scope="col" style={{ width: 'var(--space-8)' }}>
              <span className="o-visually-hidden">Select</span>
            </th>
          ) : null}
          {columns.map((column) => (
            <th
              key={column.id}
              scope="col"
              className={column.align === 'right' ? 'o-cell--right' : undefined}
              style={column.width ? { width: column.width } : undefined}
              aria-sort={
                sort?.by === column.id ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined
              }
            >
              <DefinitionPopover definition={column.hint} id={`col-${column.id}`}>
                {column.sortable && onSortChange ? (
                  <button
                    type="button"
                    className="o-btn o-btn--ghost o-btn--sm"
                    onClick={() =>
                      onSortChange({
                        by: column.id,
                        dir: sort?.by === column.id && sort.dir === 'asc' ? 'desc' : 'asc',
                      })
                    }
                  >
                    {column.header}
                  </button>
                ) : (
                  <span>{column.header}</span>
                )}
              </DefinitionPopover>
            </th>
          ))}
          {hiddenColumns.length > 0 ? (
            <th scope="col" style={{ width: 'var(--space-8)' }}>
              <span className="o-visually-hidden">More</span>
            </th>
          ) : null}
        </tr>
      </thead>
      <tbody ref={parentRef}>
        {paddingTop > 0 ? (
          <tr style={{ height: paddingTop }} aria-hidden="true">
            <td colSpan={totalCols} />
          </tr>
        ) : null}

        {indexes.map((index) => {
          const row = rows[index];
          if (!row) return null;
          const key = String(rowKey(row));
          const isExpanded = expanded.has(key);
          return (
            <Fragment key={key}>
              <tr
                data-clickable={onRowClick ? 'true' : 'false'}
                data-focused={nav.index === index ? 'true' : undefined}
                aria-selected={selection ? selection.selected.has(rowKey(row)) : undefined}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {selection ? (
                  <td>
                    <input
                      type="checkbox"
                      checked={selection.selected.has(rowKey(row))}
                      onChange={() => selection.onToggle(rowKey(row))}
                      onClick={(event) => event.stopPropagation()}
                      aria-label={`Select ${key}`}
                    />
                  </td>
                ) : null}
                {columns.map((column) => (
                  <td
                    key={column.id}
                    className={[
                      column.align === 'right' ? 'o-cell--right' : '',
                      column.mono ? 'o-cell--mono' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {column.render(row)}
                  </td>
                ))}
                {hiddenColumns.length > 0 ? (
                  <td>
                    <button
                      type="button"
                      className="o-btn o-btn--ghost o-btn--sm"
                      aria-expanded={isExpanded}
                      aria-label={isExpanded ? 'Hide more columns' : 'Show more columns'}
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleExpanded(key);
                      }}
                    >
                      {isExpanded ? 'Less' : 'More'}
                    </button>
                  </td>
                ) : null}
              </tr>
              {isExpanded && hiddenColumns.length > 0 ? (
                <tr>
                  <td colSpan={totalCols}>
                    <div className="o-cards__more">
                      {hiddenColumns.map((column) => (
                        <div key={column.id} className="o-kv">
                          <span className="o-kv__key">{column.header}</span>
                          <span className="o-kv__value">{column.render(row)}</span>
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              ) : null}
            </Fragment>
          );
        })}

        {paddingBottom > 0 ? (
          <tr style={{ height: paddingBottom }} aria-hidden="true">
            <td colSpan={totalCols} />
          </tr>
        ) : null}
      </tbody>
    </table>
  );
}

/**
 * The card list a table becomes below 768px. Priority 1 forms the card: the first column is
 * the title line, the first right aligned priority 1 column sits on that line, the rest of
 * priority 1 becomes line two. Priority 2 is the footer. Priority 3 and above live in the
 * expander. Column headers collapse into one sort control.
 */
function CardList<T, K extends string | number>({
  columns,
  rows,
  rowKey,
  onRowClick,
  selection,
  expanded,
  toggleExpanded,
  caption,
  sort,
  onSortChange,
  nav,
}: BodyProps<T, K>) {
  const p1 = columns.filter((c) => c.priority === 1);
  const p2 = columns.filter((c) => c.priority === 2);
  const rest = columns.filter((c) => c.priority >= 3);
  const title = p1[0];
  const trailing = p1.find((c) => c.align === 'right' && c.id !== title?.id);
  const lineTwo = p1.filter((c) => c.id !== title?.id && c.id !== trailing?.id);
  const sortable = columns.filter((c) => c.sortable);

  return (
    <div onKeyDown={nav.onKeyDown}>
      {sortable.length > 0 && onSortChange ? (
        <div className="o-table-tools">
          <label className="o-field__label" htmlFor={`${caption}-sort`}>
            Sort
          </label>
          <select
            id={`${caption}-sort`}
            className="o-select"
            value={sort?.by ?? sortable[0]?.id ?? ''}
            onChange={(event) => onSortChange({ by: event.target.value, dir: sort?.dir ?? 'asc' })}
          >
            {sortable.map((column) => (
              <option key={column.id} value={column.id}>
                {column.header}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      <ul className="o-cards" role="list" aria-label={caption}>
        {rows.map((row, index) => {
          const key = String(rowKey(row));
          const isExpanded = expanded.has(key);
          return (
            <li
              key={key}
              role="listitem"
              className="o-cards__item"
              data-focused={nav.index === index ? 'true' : undefined}
              aria-selected={selection ? selection.selected.has(rowKey(row)) : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              <div className="o-cards__title">
                <span className="o-cards__cell">{title ? title.render(row) : key}</span>
                {trailing ? <span className="o-mono">{trailing.render(row)}</span> : null}
              </div>
              {lineTwo.length > 0 ? (
                <div className="o-cards__line">
                  {lineTwo.map((column) => (
                    <span key={column.id} className="o-cards__cell">
                      {column.render(row)}
                    </span>
                  ))}
                </div>
              ) : null}
              {p2.length > 0 ? (
                <div className="o-cards__foot">
                  {p2.map((column) => (
                    <span key={column.id} className="o-cards__cell">
                      {column.render(row)}
                    </span>
                  ))}
                </div>
              ) : null}
              {rest.length > 0 ? (
                <>
                  <button
                    type="button"
                    className="o-cards__expander"
                    aria-expanded={isExpanded}
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleExpanded(key);
                    }}
                  >
                    {isExpanded ? 'Less' : 'More'}
                  </button>
                  {isExpanded ? (
                    <div className="o-cards__more">
                      {rest.map((column) => (
                        <div key={column.id} className="o-kv">
                          <span className="o-kv__key">{column.header}</span>
                          <span className="o-kv__value">{column.render(row)}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
